import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { platform } from './platform';
import { secureStorage } from './secureStorage';
import { verifyBiometric } from './biometric';

/**
 * Biometric-backed login persistence. Replaces reliance on the WebView's password
 * manager: the email + password are stored in the device Keychain via
 * `@capgo/capacitor-native-biometric`, and only handed back after a successful
 * biometric (Face ID / Touch ID / fingerprint) verification. A non-sensitive
 * username hint is also kept so the sign-in screen can prefill the email and decide
 * whether to offer the biometric affordance WITHOUT prompting.
 */

const SERVER = 'ip4x-resident-login';
const HINT_KEY = 'biometric_login_username';

export interface StoredLogin {
  username: string;
  password: string;
}

/** Persist the just-used credentials behind the device biometric. */
export async function saveBiometricLogin(username: string, password: string): Promise<boolean> {
  if (!platform.isNative() || !username || !password) return false;
  try {
    await NativeBiometric.setCredentials({ username, password, server: SERVER });
    await secureStorage.set(HINT_KEY, username);
    return true;
  } catch (error) {
    console.warn('[biometric-login] save failed:', error);
    return false;
  }
}

/** Remembered email (no biometric prompt) — prefill + decide whether to show the
 *  biometric sign-in button. Null when nothing is stored. */
export async function getBiometricLoginHint(): Promise<string | null> {
  if (!platform.isNative()) return null;
  try {
    return await secureStorage.get(HINT_KEY);
  } catch {
    return null;
  }
}

/** Prompt biometrics, then return the stored credentials. Null on cancel / none. */
export async function restoreBiometricLogin(reason: string): Promise<StoredLogin | null> {
  if (!platform.isNative()) return null;
  const ok = await verifyBiometric(reason);
  if (!ok) return null;
  try {
    const creds = await NativeBiometric.getCredentials({ server: SERVER });
    if (!creds?.username || !creds?.password) return null;
    return { username: creds.username, password: creds.password };
  } catch (error) {
    console.warn('[biometric-login] restore failed:', error);
    return null;
  }
}

/** Forget the stored login (explicit sign-out / disable). */
export async function clearBiometricLogin(): Promise<void> {
  if (!platform.isNative()) return;
  try {
    await NativeBiometric.deleteCredentials({ server: SERVER });
  } catch {
    /* noop */
  }
  await secureStorage.remove(HINT_KEY);
}
